
import React, { useContext } from 'react';
import { AppContext } from '../contexts/AppContext';
import HeaderLanding from '../components/HeaderLanding';
import FooterLanding from '../components/FooterLanding';

const FeatureCard: React.FC<{ title: string; description: string; icon: string }> = ({ title, description, icon }) => (
    <div className="bg-white p-6 rounded-lg shadow-lg text-center transform hover:-translate-y-2 transition-transform duration-300">
        <div className="text-blue-600 mb-4">
            <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={icon}></path></svg>
        </div>
        <h3 className="text-xl font-bold text-gray-800 mb-2">{title}</h3>
        <p className="text-gray-600">{description}</p>
    </div>
);

const PricingCard: React.FC<{ plan: string; price: string; features: string[]; recommended?: boolean }> = ({ plan, price, features, recommended }) => {
    const { setUnauthenticatedView } = useContext(AppContext);
    return (
        <div className={`border rounded-lg p-8 flex flex-col ${recommended ? 'border-blue-600 border-2' : 'border-gray-200'}`}>
            {recommended && <span className="bg-blue-600 text-white text-xs font-bold px-3 py-1 rounded-full self-start mb-4">RECOMENDADO</span>}
            <h3 className="text-2xl font-bold text-gray-800">{plan}</h3>
            <p className="text-4xl font-extrabold my-4 text-gray-900">{price}<span className="text-base font-medium text-gray-500">/mes</span></p>
            <ul className="space-y-4 text-gray-600 mb-8 flex-grow">
                {features.map(feature => (
                    <li key={feature} className="flex items-center">
                        <svg className="w-5 h-5 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7"></path></svg>
                        {feature}
                    </li>
                ))}
            </ul>
            <a 
                href="#"
                onClick={(e) => { e.preventDefault(); setUnauthenticatedView('onboarding'); }}
                className={`w-full text-center font-semibold py-3 rounded-lg transition-colors ${recommended ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-gray-100 text-blue-600 hover:bg-gray-200'}`}
            >
                Empezar Ahora
            </a>
        </div>
    );
};

const LandingScreen: React.FC = () => {
    const { setUnauthenticatedView } = useContext(AppContext);
    return (
        <div className="bg-gray-50 text-gray-800">
            <HeaderLanding />

            <main>
                {/* Hero Section */}
                <section className="text-center py-20 md:py-32 bg-white">
                    <div className="container mx-auto px-6">
                        <h1 className="text-4xl md:text-6xl font-extrabold text-gray-900 leading-tight">
                            Dejá atrás el Excel. <br />
                            <span className="text-blue-600">Gestión moderna para tu club.</span>
                        </h1>
                        <p className="mt-6 text-lg md:text-xl text-gray-600 max-w-2xl mx-auto">
                            Socios, caja, puntos y reportes. Todo en un solo lugar, increíblemente fácil de usar para que te enfoques en lo que más importa: tus socios.
                        </p>
                        <a 
                            href="#"
                            onClick={(e) => { e.preventDefault(); setUnauthenticatedView('onboarding'); }} 
                            className="mt-10 inline-block bg-blue-600 text-white font-bold text-lg px-8 py-4 rounded-full hover:bg-blue-700 transition-transform transform hover:scale-105 shadow-lg"
                        >
                            Empezar prueba gratis por 14 días
                        </a>
                        <div className="mt-4">
                            <a href="#" onClick={(e) => { e.preventDefault(); setUnauthenticatedView('login-socio'); }} className="text-sm text-gray-700 underline">Acceso para socios</a>
                        </div>
                    </div>
                </section>

                {/* Features Section */}
                <section id="features" className="py-20 md:py-28">
                    <div className="container mx-auto px-6">
                        <div className="text-center mb-16">
                            <h2 className="text-3xl md:text-4xl font-bold text-gray-900">¿Te suena familiar este dolor de cabeza?</h2>
                            <p className="mt-4 text-lg text-gray-600 max-w-2xl mx-auto">Tu Club fue diseñado para eliminar el caos y devolverte el control.</p>
                        </div>
                        <div className="grid md:grid-cols-3 gap-8">
                            <FeatureCard 
                                icon="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                                title="Gestión de Socios Centralizada"
                                description="Olvidate de las planillas. Tené una base de datos única, actualizada y accesible de todos tus miembros."
                            />
                            <FeatureCard 
                                icon="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
                                title="Control de Caja y Ventas"
                                description="Registrá cada venta en segundos. Hacé cierres de caja perfectos y tené claro cada movimiento de dinero."
                            />
                            <FeatureCard 
                                icon="M5 5a2 2 0 012-2h10a2 2 0 012 2v1h-14v-1zM19 19a2 2 0 01-2 2H7a2 2 0 01-2-2V9h14v10zM11 14v-2m-3 2v-2"
                                title="Fidelización con Puntos"
                                description="Premiá a tus socios más leales con un sistema de puntos automático y fácil de gestionar. ¡Hacelos volver!"
                            />
                        </div>
                    </div>
                </section>

                {/* Pricing Section */}
                <section id="pricing" className="py-20 md:py-28 bg-white">
                    <div className="container mx-auto px-6">
                        <div className="text-center mb-16">
                            <h2 className="text-3xl md:text-4xl font-bold text-gray-900">Un plan para cada tamaño de club</h2>
                            <p className="mt-4 text-lg text-gray-600 max-w-2xl mx-auto">Simple, transparente y sin contratos a largo plazo.</p>
                        </div>
                        <div className="grid lg:grid-cols-3 gap-8 max-w-4xl mx-auto">
                            <PricingCard 
                                plan="Starter"
                                price="$19.99"
                                features={['Hasta 200 socios', 'Gestión de Caja', 'Soporte por email']}
                            />
                            <PricingCard 
                                plan="Pro"
                                price="$49.99"
                                features={['Hasta 600 socios', 'Sistema de Puntos', 'Reportes Avanzados', 'Soporte Prioritario']}
                                recommended
                            />
                            <PricingCard 
                                plan="Elite"
                                price="$99.99"
                                features={['Socios ilimitados', 'Todo de Pro', 'Portal del Socio', 'Soporte 24/7']}
                            />
                        </div>
                    </div>
                </section>
                
                 {/* Final CTA Section */}
                <section id="contact" className="py-20 md:py-32">
                    <div className="container mx-auto px-6 text-center">
                         <h2 className="text-3xl md:text-4xl font-bold text-gray-900">¿Listo para transformar tu club?</h2>
                         <p className="mt-4 text-lg text-gray-600 max-w-2xl mx-auto">
                           Empezá hoy mismo y descubrí por qué decenas de clubes confían en nosotros para su gestión diaria.
                        </p>
                        <a 
                            href="#"
                            onClick={(e) => { e.preventDefault(); setUnauthenticatedView('onboarding'); }}
                            className="mt-10 inline-block bg-blue-600 text-white font-bold text-lg px-8 py-4 rounded-full hover:bg-blue-700 transition-transform transform hover:scale-105 shadow-lg"
                        >
                            Comenzar sin costo
                        </a>
                    </div>
                </section>

            </main>

            <FooterLanding />
        </div>
    );
};

export default LandingScreen;